import { RAW_cssValue, ThemeUIStyleObject } from "@classdojo/web/nessie/stylingLib";
import { translate } from "@classdojo/web/utils/translate";
import Audio from "./Audio";
import { RadioChannel, SendRadioProps } from "src/pods/radio";
import MaterialIcon from "src/shared/components/MaterialIcon";
import logEvent from "src/utils/logEvent";
import useAfterUnmount from "@classdojo/web/hooks/useAfterUnmount";
import useOnFirstRender from "@classdojo/web/hooks/useOnFirstRender";

import radio from "src/pods/radio/assets/blue-radio.png";

type RadioDisplayProps = {
  sendRadio?: (params: SendRadioProps) => void;
  channel?: RadioChannel;
  currentTrackIndex?: number;
  displayClose?: VoidFunction;
  playing?: boolean;
};

const RadioDisplay = (props: RadioDisplayProps) => {
  // Cannot use useLogEventOnFirstRender because we need toolkit/src/utils/logEvent to be
  // used. That function takes into account the role but also the platform to decide how
  // the event must be logged.
  useOnFirstRender(() => {
    logEvent({
      eventName: "radio.result.open",
    });
  });

  const stop = () => {
    props.sendRadio?.({ channel: props.channel });
    props.displayClose?.();
  };

  const togglePlayback = () => {
    const { playing, sendRadio } = props;
    sendRadio?.({ currentTrackIndex: props.currentTrackIndex, playing: !playing, channel: props.channel });
  };

  const nextTrack = () => {
    const { sendRadio, currentTrackIndex, channel: currentChannel } = props;
    if (currentChannel && currentTrackIndex !== undefined) {
      sendRadio?.({
        channel: props.channel,
        currentTrackIndex: (currentTrackIndex + 1) % currentChannel.tracks.length,
        playing: true,
      });
    }
  };

  const previousTrack = () => {
    const { sendRadio, currentTrackIndex, channel: currentChannel } = props;
    if (currentChannel && currentTrackIndex !== undefined) {
      sendRadio?.({
        channel: props.channel,
        currentTrackIndex: (currentTrackIndex + (currentChannel.tracks.length - 1)) % currentChannel.tracks.length,
        playing: true,
      });
    }
  };

  const onPlaying = () => {
    if (!props.playing) {
      props.sendRadio?.({
        channel: props.channel,
        currentTrackIndex: props.currentTrackIndex,
        playing: true,
      });
    }
  };

  const onPause = () => {
    if (props.playing) {
      props.sendRadio?.({
        channel: props.channel,
        currentTrackIndex: props.currentTrackIndex,
        playing: false,
      });
    }
  };

  const onEnded = () => {
    nextTrack();
  };

  useAfterUnmount(() => {
    stop();
    logEvent({ eventName: "radio.result.close" });
  });

  const { channel, currentTrackIndex, playing } = props;
  const hasAudioSrc = channel && typeof currentTrackIndex === "number" && channel.tracks[currentTrackIndex].src;

  return (
    <section>
      <div sx={radioImageContainerStyle} onClick={togglePlayback}>
        <img src={radio} alt="" />
      </div>

      <div sx={playbackControlStyle}>
        <button
          data-name="radio_display:skip_previous"
          sx={buttonResetStyles}
          onClick={previousTrack}
          aria-label={translate({
            str: "dojo.toolkit:music.previous",
            fallback: "Previous",
          })}
        >
          <MaterialIcon icon="skip_previous" sx={{ ...playbackSideButton, iconStyle }} />
        </button>
        {playing ? (
          <button
            data-name="radio_display:pause_button"
            sx={buttonResetStyles}
            onClick={togglePlayback}
            aria-label={translate("dojo.toolkit:music.pause", "Pause")}
          >
            <MaterialIcon icon={"pause"} sx={playbackMainButton} />
          </button>
        ) : (
          <button
            data-name="radio_display:play_button"
            sx={buttonResetStyles}
            onClick={togglePlayback}
            aria-label={translate("dojo.toolkit:music.play", "Play")}
          >
            <MaterialIcon icon={"play_arrow"} sx={playbackMainButton} />
          </button>
        )}
        <button
          data-name="radio_display:skip_next"
          sx={buttonResetStyles}
          onClick={nextTrack}
          aria-label={translate({
            str: "dojo.toolkit:music.next",
            fallback: "Next",
          })}
        >
          <MaterialIcon icon="skip_next" sx={{ ...playbackSideButton, iconStyle }} />
        </button>
      </div>
      {hasAudioSrc && (
        <Audio
          src={channel.tracks[currentTrackIndex].src}
          isPlaying={!!props.playing}
          onPlaying={onPlaying}
          onPause={onPause}
          onEnded={onEnded}
        />
      )}
    </section>
  );
};

export default RadioDisplay;

const radioImageContainerStyle: ThemeUIStyleObject = {
  display: "block",
  position: "relative",
  margin: RAW_cssValue("0 auto"),
  cursor: "pointer",
};

const playbackControlStyle: ThemeUIStyleObject = {
  margin: RAW_cssValue("0 auto"),
  textAlign: "center",
  position: "absolute",
  bottom: 0,
  left: 0,
  width: "100%",
};

const iconStyle: ThemeUIStyleObject = {
  verticalAlign: "middle",
};

const playbackMainButton: ThemeUIStyleObject = {
  display: "inline-block",
  width: "64px",
  height: "64px",
  margin: RAW_cssValue("13px"),
  borderRadius: "dt_radius_m",
  fontSize: "40px",
  lineHeight: "64px",
  textAlign: "center",
  verticalAlign: "middle",
  backgroundColor: "dt_content_tertiary",
  color: "dt_content_warning",
  cursor: "pointer",
};

const playbackSideButton: ThemeUIStyleObject = {
  display: "inline-block",
  width: "30px",
  margin: RAW_cssValue("13px"),
  fontSize: "24px",
  lineHeight: "24px",
  verticalAlign: "middle",
  fontWeight: "600",
  color: "dt_content_tertiary",
  cursor: "pointer",
};

const buttonResetStyles: ThemeUIStyleObject = {
  margin: 0,
  padding: 0,
  border: "none",
  backgroundColor: "transparent",
};
